import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import Wave from "../wave";
import theme from "../../styles/theme";
import { useTranslation } from "react-i18next";
import {
  ArrowForward,
  CheckroomOutlined,
  CropFree,
  CropSquareOutlined,
  DirectionsCarFilledOutlined,
  LayersOutlined,
  LocalOfferOutlined,
  RedeemOutlined,
} from "@mui/icons-material";

const FeatureSection = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: <CheckroomOutlined />,
      title: t("Home.Feature.Cards.Textile.Title", "Tekstiltrykk"),
      desc: t(
        "Home.Feature.Cards.Textile.Desc",
        "DTF trykk på t-skjorter, hettegensere og arbeidsklær.",
      ),
      href: "/services/tekstiltrykk",
    },
    {
      icon: <LayersOutlined />,
      title: t("Home.Feature.Cards.Dtf.Title", "DTF Transfer"),
      desc: t(
        "Home.Feature.Cards.Dtf.Desc",
        "Bestill ferdige transfer som kan presses på klær.",
      ),
      href: "/services/dtf-transfer",
    },
    {
      icon: <LocalOfferOutlined />,
      title: t("Home.Feature.Cards.Stickers.Title", "Klistremerker"),
      desc: t(
        "Home.Feature.Cards.Stickers.Desc",
        "Skreddersydde stickers i alle størrelser og former.",
      ),
      href: "/services/klistremerker",
    },
    {
      icon: <CropFree />,
      title: t("Home.Feature.Cards.LargeFormat.Title", "Storformat print"),
      desc: t(
        "Home.Feature.Cards.LargeFormat.Desc",
        "Bannere, skilt, plakater og veggdekor.",
      ),
      href: "/services/storformat",
    },
    {
      icon: <DirectionsCarFilledOutlined />,
      title: t("Home.Feature.Cards.Foil.Title", "Foliering"),
      desc: t(
        "Home.Feature.Cards.Foil.Desc",
        "Bilprofilering, vindusdekor og bedriftsprofilering.",
      ),
      href: "/services/foliering",
    },
    {
      icon: <RedeemOutlined />,
      title: t("Home.Feature.Cards.ProfileProducts.Title", "Profilprodukter"),
      desc: t(
        "Home.Feature.Cards.ProfileProducts.Desc",
        "Kopper, klær og giveaways med logo.",
      ),
      href: "/services/profilprodukter",
    },
  ];

  return (
    <>
      <Wave
        amplitude={60}
        duration={5}
        fillStyle={theme.palette.primary.subtle}
        frequency={3.0}
        waveHeightRatio={0.3}
      />

      <Box
        sx={{
          pt: { xs: 7, md: 12 },
          pb: { xs: 7, md: 22 },
          backgroundColor: "primary.subtle",
          color: "#fff",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Typography
              variant="h3"
              component="h2"
              fontWeight={800}
              sx={{ mb: 1 }}
            >
              {t("Home.Feature.Title", "Våre tjenester")}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              {t(
                "Home.Feature.Desc",
                "Alt du trenger for å skape et sterkt og profesjonelt inntrykk",
              )}
            </Typography>
          </Box>

          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {services.map((service, index) => (
              <Grid key={service.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  onClick={() => window.location.assign(service.href)}
                  sx={{
                    borderRadius: 4,
                    backgroundColor: "background.paper",
                    height: "100%",
                    width: "100%",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "transform 180ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 38px rgba(0,0,0,0.12)",
                    },

                    "&:hover .seeMoreButton": {
                      color: "primary.main",
                    },
                    "&:hover .seeMoreArrow": {
                      transform: "translateX(4px)",
                    },
                    "& .seeMoreArrow": {
                      transition: "transform 180ms ease",
                    },
                    "& .seeMoreButton": {
                      transition: "color 180ms ease",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 193, 7, 0.18)",
                        color: "warning.main",
                        mb: 2.2,
                        "& svg": { fontSize: 26 },
                      }}
                    >
                      {service.icon}
                    </Box>

                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                      {service.title}
                    </Typography>

                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2.2 }}>
                      {service.desc}
                    </Typography>

                    <Link
                      href={service.href}
                      underline="none"
                      className="seeMoreButton"
                      alignSelf={"flex-end"}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 400,
                        mt: "auto",
                        color: "text.secondary",
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("Home.Feature.ReadMore", "Les mer")}
                      <ArrowForward
                        className="seeMoreArrow"
                        sx={{ fontSize: 18 }}
                      />
                    </Link>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Wave
        amplitude={60}
        duration={10}
        fillStyle={theme.palette.background.default2}
        frequency={2}
        waveHeightRatio={0.3}
        backgroundColor={theme.palette.primary.subtle}
      />
    </>
  );
};

export default FeatureSection;
